import React, { useState, useEffect, useRef } from 'react';
import { LeboncoinParsedData } from '../../services/leboncoinService';
import { X, User, Home, Wallet, Search, MapPin, Building, UserPlus, Check, ChevronDown, Bath, BedDouble, Users, Ruler, Phone, Mail, Globe, Briefcase, Key, Sparkles, AlertCircle, Bell, HelpCircle, ExternalLink } from 'lucide-react';
import { Deal, Owner, Property, PipelineType, DealStage, LeadSource, Agency, UserProfile } from '../../types';
import { useModal } from '../ui/ModalProvider';
import { leboncoinService } from '../../services/leboncoinService';
import { SearchableSelect } from '../ui/SearchableSelect';
import { formatPhoneForStorage, formatPhoneForDisplay, handlePhoneInputChange } from '../../lib/phoneUtils';

interface AddDealModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (deal: Deal, owner?: Owner, property?: Property) => Promise<void>;
  owners: Owner[];
  properties: Property[];
  agencies: Agency[];
  userProfile: UserProfile | null;
  initialOwnerId?: string;
  initialPropertyId?: string;
}

type Mode = 'existing_owner' | 'new_owner';
type PropertyMode = 'existing_property' | 'new_property';

export const AddDealModal: React.FC<AddDealModalProps> = ({ isOpen, onClose, onSave, owners, properties, agencies, userProfile, initialOwnerId, initialPropertyId }) => {
  const { toast } = useModal();
  const [loading, setLoading] = useState(false);
  
  // Refs pour éviter les traitements multiples
  const processedMessagesRef = useRef<Set<string>>(new Set());
  const isParsingLbcRef = useRef(false);
  const isOpenRef = useRef(isOpen);

  // Écouter les messages de l'extension Chrome
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'PLACID_OPEN_DEAL_MODAL') {
        const data = event.data.data;
        const html = event.data.html;
        const url = event.data.url || data?.url;
        
        // Créer un identifiant unique pour ce message
        const messageId = `${url}-${html?.substring(0, 50)}`;
        
        // Éviter de traiter le même message plusieurs fois
        if (processedMessagesRef.current.has(messageId) || isParsingLbcRef.current) {
          return;
        }
        
        processedMessagesRef.current.add(messageId);
        
        // Nettoyer les anciens messages après 5 secondes
        setTimeout(() => {
          processedMessagesRef.current.delete(messageId);
        }, 5000);
        
        // Si on a le HTML, parser avec Gemini
        if (html && url) {
          handleParseLeboncoinWithHTML(url, html, true); // fromExtension = true
        } else if (data) {
          // Sinon, utiliser les données déjà extraites
          handleParseLeboncoinData(data, true); // fromExtension = true
        }
      }
    };

    // Écouter aussi les messages chrome.runtime
    if (typeof window !== 'undefined' && (window as any).chrome && (window as any).chrome.runtime && (window as any).chrome.runtime.onMessage) {
      (window as any).chrome.runtime.onMessage.addListener((request: any, sender: any, sendResponse: (response: any) => void) => {
        if (request.type === 'PLACID_OPEN_DEAL_MODAL') {
          const html = request.html;
          const url = request.url || request.data?.url;

          // Créer un identifiant unique pour ce message
          const messageId = `${url}-${html?.substring(0, 50)}`;
          
          // Éviter de traiter le même message plusieurs fois
          if (processedMessagesRef.current.has(messageId) || isParsingLbcRef.current) {
            sendResponse({ success: false, error: 'Already processing' });
            return true;
          }
          
          processedMessagesRef.current.add(messageId);
          
          // Nettoyer les anciens messages après 5 secondes
          setTimeout(() => {
            processedMessagesRef.current.delete(messageId);
          }, 5000);

          if (html && url) {
            handleParseLeboncoinWithHTML(url, html, true); // fromExtension = true
          } else if (request.data) {
            handleParseLeboncoinData(request.data, true); // fromExtension = true
          }
          sendResponse({ success: true });
        }
        return true;
      });
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Fonction pour parser avec Gemini depuis le HTML
  const handleParseLeboncoinWithHTML = async (url: string, html: string, fromExtension: boolean = false) => {
    // Éviter les appels multiples simultanés
    if (isParsingLbc) {
      console.log('Déjà en cours de parsing, ignore...');
      return;
    }
    
    // Définir l'URL en lecture seule si elle vient de l'extension
    if (fromExtension) {
      setIsLeboncoinUrlReadOnly(true);
      setLeboncoinUrl(url);
      setShowLeboncoinImport(true); // Afficher le champ URL quand on reçoit des données de l'extension
    }
    
    setIsParsingLbc(true);
    try {
      const parsedData = await leboncoinService.parseAdWithGemini(url, html);
      await handleParseLeboncoinData(parsedData, fromExtension);
      toast.success('Données Leboncoin extraites avec Gemini !');
      // Marquer l'analyse comme terminée et masquer les champs
      setLeboncoinAnalysisComplete(true);
      setShowLeboncoinImport(false);
    } catch (error: any) {
      console.error('Error parsing with Gemini:', error);
      
      // Détecter l'erreur de quota et arrêter le processus
      if (error.message && error.message.includes('QUOTA_EXCEEDED')) {
        const quotaMessage = error.message.replace('QUOTA_EXCEEDED: ', '');
        toast.error(quotaMessage, { duration: 10000 }); // Message plus long pour le quota
        // Ne pas réessayer automatiquement en cas de quota - arrêter immédiatement
        setIsParsingLbc(false);
        isParsingLbcRef.current = false; // Mettre à jour la ref aussi
        return; // Arrêter ici pour éviter les tentatives multiples
      } else {
        toast.error('Erreur lors de l\'extraction par Gemini: ' + (error.message || 'Erreur inconnue'));
      }
    } finally {
      setIsParsingLbc(false);
      isParsingLbcRef.current = false; // Mettre à jour la ref aussi
    }
  };

  // Fonction pour préremplir avec les données de l'extension
  const handleParseLeboncoinData = async (parsedData: LeboncoinParsedData, fromExtension: boolean = false) => {
    // Si la modale n'est pas encore ouverte, attendre un peu
    // La modale devrait être ouverte par Pipeline.tsx avant l'envoi du message
    if (!isOpenRef.current) {
      // Attendre que la modale soit ouverte (elle devrait l'être très rapidement)
      let attempts = 0;
      const maxAttempts = 30; // 3 secondes max (30 * 100ms)
      await new Promise<void>((resolve) => {
        const checkInterval = setInterval(() => {
          attempts++;
          if (isOpenRef.current || attempts >= maxAttempts) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
      });
      
      // Si après plusieurs tentatives la modale n'est toujours pas ouverte, logger une erreur mais continuer
      if (!isOpenRef.current) {
        console.warn('La modale n\'est pas encore ouverte, mais on continue le préremplissage');
      }
    }

    // Build note content - ne pas tronquer la description
    let noteContent = `🔗 Annonce Leboncoin: ${parsedData.url}`;
    if (parsedData.adId) noteContent += `\nID: ${parsedData.adId}`;
    if (parsedData.title) noteContent += `\n\n📋 ${parsedData.title}`;
    if (parsedData.monthlyRentWithCharges) noteContent += `\n💰 Loyer CC: ${parsedData.monthlyRentWithCharges}€`;
    if (parsedData.location) noteContent += `\n📍 Localisation: ${parsedData.location}`;
    if (parsedData.surface) noteContent += `\n📐 Surface: ${parsedData.surface}m²`;
    if (parsedData.bedrooms) noteContent += `\n🚪 Chambres: ${parsedData.bedrooms}`;
    if (parsedData.description) noteContent += `\n\n📝 Description:\n${parsedData.description}`;

    // Set default values
    setDealData(prev => ({
        ...prev,
        type: PipelineType.SUBLET,
        source: 'Leboncoin',
        value: 0, // Ne pas mettre le loyer dans value, seulement dans monthlyRentWithCharges
        monthlyRentWithCharges: parsedData.monthlyRentWithCharges || parsedData.price || undefined,
        needsCompletion: true, // Marquer comme nécessitant une complétion par défaut
        notes: [{
            id: Date.now().toString(),
            content: noteContent,
            createdAt: new Date().toISOString(),
            authorName: 'Système' // Toujours "Système" pour les données LBC
        }]
    }));

    // Préremplir le propriétaire avec les données extraites
    setNewOwner(prev => ({
        ...prev,
        firstName: parsedData.ownerFirstName || 'A définir',
        lastName: parsedData.ownerLastName || parsedData.ownerPseudo || '',
        source: 'Leboncoin',
        newsletterOptin: prev.newsletterOptin || false
    }));

    // Prefill property - utiliser directement zipCode et city (toujours disponibles sur LBC)
    setNewProperty(prev => ({
        ...prev,
        address: parsedData.address || '', // Adresse complète ou quartier
        zipCode: parsedData.zipCode || '', // Code postal (toujours disponible)
        city: parsedData.city || '', // Ville (toujours disponible)
    }));

    if (parsedData.surface) {
        setNewProperty(prev => ({ ...prev, surface: parsedData.surface }));
    }

    if (parsedData.bedrooms !== undefined) {
        setNewProperty(prev => ({
            ...prev,
            bedrooms: parsedData.bedrooms || 0,
            bathrooms: parsedData.bathrooms || 1,
            maxGuests: parsedData.maxGuests || 2
        }));
    }

    toast.success('Données Leboncoin préremplies !');
  };
  
  // --- STATE DE NAVIGATION ---
  const [mode, setMode] = useState<Mode>('new_owner');
  const [propertyMode, setPropertyMode] = useState<PropertyMode>('new_property');

  // --- STATE RECHERCHE PROPRIÉTAIRE ---
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);

  // --- STATE NOUVELLES DONNÉES ---
  // Owner
  const [newOwner, setNewOwner] = useState<Partial<Owner>>({
      firstName: '', lastName: '', email: '', phone: '', source: 'Site Web', newsletterOptin: false
  });
  
  // Property
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
  const [newProperty, setNewProperty] = useState<Partial<Property>>({
      address: '', city: '', zipCode: '', type: 'Appartement', surface: 0, bedrooms: 1, bathrooms: 1, maxGuests: 2
  });
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);

  // Deal (Shared)
  const [dealData, setDealData] = useState<Partial<Deal>>({
      type: PipelineType.CONCIERGE,
      value: 0,
      stage: DealStage.NEW,
      agencyId: ''
  });

  // Leboncoin URL import
  const [leboncoinUrl, setLeboncoinUrl] = useState('');
  const [isParsingLbc, setIsParsingLbc] = useState(false);
  const [isLeboncoinUrlReadOnly, setIsLeboncoinUrlReadOnly] = useState(false);
  const [showLeboncoinImport, setShowLeboncoinImport] = useState(false);
  const [leboncoinAnalysisComplete, setLeboncoinAnalysisComplete] = useState(false);

  // --- PERMISSIONS ---
  const canSelectAgency = userProfile?.role === 'admin' || userProfile?.accessLevel === 'national';

  // --- DÉTECTION DES DONNÉES INCOMPLÈTES (LBC) ---
  const isFromLeboncoin = dealData.source === 'Leboncoin' || newOwner.source === 'Leboncoin';
  
  const getIncompleteFields = () => {
    const incomplete: string[] = [];
    
    // Vérifier le propriétaire
    if (mode === 'new_owner') {
      if (newOwner.firstName === 'A définir' || !newOwner.firstName) {
        incomplete.push('Prénom du propriétaire');
      }
      if (!newOwner.email || newOwner.email.trim() === '') {
        incomplete.push('Email du propriétaire');
      }
      if (!newOwner.phone || newOwner.phone.trim() === '') {
        incomplete.push('Téléphone du propriétaire');
      }
    }
    
    // Vérifier le bien
    if (propertyMode === 'new_property') {
      if (!newProperty.address || newProperty.address.trim() === '') {
        incomplete.push('Adresse complète du bien');
      }
      if (!newProperty.surface || newProperty.surface === 0) {
        incomplete.push('Surface du bien');
      }
    }
    
    return incomplete;
  };
  
  const incompleteFields = getIncompleteFields();
  const hasIncompleteData = isFromLeboncoin && incompleteFields.length > 0;

  // Synchroniser la ref isOpen avec l'état
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Synchroniser la ref avec l'état
  useEffect(() => {
    isParsingLbcRef.current = isParsingLbc;
  }, [isParsingLbc]);

  // --- EFFECTS ---
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    } else if (initialOwnerId || initialPropertyId) {
      // Pré-remplir avec les props initiales
      if (initialOwnerId) {
        const owner = owners.find(o => o.id === initialOwnerId);
        if (owner) {
          setSelectedOwner(owner);
          setMode('existing_owner');
          
          // Si un bien est aussi spécifié, le pré-sélectionner
          if (initialPropertyId) {
            const property = properties.find(p => p.id === initialPropertyId && p.ownerId === owner.id);
            if (property) {
              setSelectedPropertyId(initialPropertyId);
              setPropertyMode('existing_property');
            } else {
              setPropertyMode('new_property');
            }
          } else {
            // Si pas de bien spécifié, vérifier s'il y a des biens existants pour ce propriétaire
            const ownerProps = properties.filter(p => p.ownerId === owner.id);
            if (ownerProps.length > 0) {
              setPropertyMode('existing_property');
              setSelectedPropertyId(ownerProps[0].id!);
            } else {
              setPropertyMode('new_property');
            }
          }
        }
      }
    }
  }, [isOpen, initialOwnerId, initialPropertyId, owners, properties]);

  useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
          if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
              setShowSuggestions(false);
          }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const resetForm = () => {
    setMode('new_owner');
    setPropertyMode('new_property');
    setSearchQuery('');
    setSelectedOwner(null);
    setSelectedPropertyId('');
    setNewOwner({ firstName: '', lastName: '', email: '', phone: '', source: 'Site Web', newsletterOptin: false });
    setNewProperty({ address: '', city: '', zipCode: '', type: 'Appartement', surface: 0, bedrooms: 1, bathrooms: 1, maxGuests: 2 });
    setDealData({ type: PipelineType.CONCIERGE, value: 0, stage: DealStage.NEW, agencyId: '', monthlyRentWithCharges: undefined });
    setAddressSuggestions([]);
    setLeboncoinUrl('');
    setIsParsingLbc(false);
    setIsLeboncoinUrlReadOnly(false);
    setShowLeboncoinImport(false);
    setLeboncoinAnalysisComplete(false);
    processedMessagesRef.current.clear(); // Nettoyer les messages traités
  };

  // Parse Leboncoin URL and prefill form
  const handleParseLeboncoinUrl = async (url: string) => {
      if (!url || !url.includes('leboncoin.fr')) {
          toast.error("URL Leboncoin invalide");
          return;
      }

      setIsParsingLbc(true);
      try {
          // Call service to parse LBC page
          const parsedData = await leboncoinService.parseAd(url);

          // Extract ad ID from URL
          const adIdMatch = url.match(/\/(\d+)\.htm/);
          const adId = adIdMatch ? adIdMatch[1] : parsedData.adId;

          // Build note content with all extracted info
          let noteContent = `🔗 Annonce Leboncoin: ${url}`;
          if (adId) noteContent += `\nID: ${adId}`;
          
          // Add warning if scraping was not successful
          if (parsedData.note) {
              noteContent += `\n\n${parsedData.note}`;
          }
          
          // Add extracted data if available
          if (parsedData.title) noteContent += `\n\n📋 ${parsedData.title}`;
          if (parsedData.price) noteContent += `\n💰 Prix: ${parsedData.price}€`;
          if (parsedData.location) noteContent += `\n📍 Localisation: ${parsedData.location}`;
          if (parsedData.surface) noteContent += `\n📐 Surface: ${parsedData.surface}m²`;
          if (parsedData.bedrooms) noteContent += `\n🚪 Chambres: ${parsedData.bedrooms}`;
          if (parsedData.description) noteContent += `\n\n📝 Description:\n${parsedData.description}`;

          // Set default values for LBC
          setDealData(prev => ({
              ...prev,
              type: PipelineType.SUBLET, // Sous-location par défaut
              source: 'Leboncoin',
              value: 0, // Ne pas mettre le loyer dans value, seulement dans monthlyRentWithCharges
              monthlyRentWithCharges: parsedData.monthlyRentWithCharges || parsedData.price || undefined,
              needsCompletion: true, // Marquer comme nécessitant une complétion par défaut
              notes: [{
                  id: Date.now().toString(),
                  content: noteContent,
                  createdAt: new Date().toISOString(),
                  authorName: 'Système' // Toujours "Système" pour les données LBC
              }]
          }));

          // Préremplir le propriétaire avec les données extraites
          setNewOwner(prev => ({
              ...prev,
              firstName: parsedData.ownerFirstName || 'A définir',
              lastName: parsedData.ownerLastName || parsedData.ownerPseudo || '',
              source: 'Leboncoin'
          }));

          // Prefill property data - utiliser directement zipCode et city (toujours disponibles sur LBC)
          setNewProperty(prev => ({
              ...prev,
              address: parsedData.address || '', // Adresse complète ou quartier
              zipCode: parsedData.zipCode || '', // Code postal (toujours disponible)
              city: parsedData.city || '', // Ville (toujours disponible)
          }));

          if (parsedData.surface) {
              setNewProperty(prev => ({
                  ...prev,
                  surface: parsedData.surface
              }));
          }

          // Utiliser les données converties (bedrooms, bathrooms, maxGuests) si disponibles
          if (parsedData.bedrooms !== undefined) {
              setNewProperty(prev => ({
                  ...prev,
                  bedrooms: parsedData.bedrooms || 0,
              }));
          }

          if (parsedData.bathrooms) {
              setNewProperty(prev => ({
                  ...prev,
                  bathrooms: parsedData.bathrooms
              }));
          }

          if (parsedData.maxGuests) {
              setNewProperty(prev => ({
                  ...prev,
                  maxGuests: parsedData.maxGuests
              }));
          }

          // Marquer l'analyse comme terminée et masquer les champs
          setLeboncoinAnalysisComplete(true);
          setShowLeboncoinImport(false);
          
          // Show appropriate success message
          if (parsedData.note) {
              toast.warning(`Annonce Leboncoin détectée ! ${parsedData.adId ? `ID: ${parsedData.adId}` : ''} - Veuillez compléter les informations manuellement.`);
          } else {
              toast.success(`Annonce Leboncoin analysée ! ${parsedData.title ? `"${parsedData.title}"` : ''} - Type défini en 'Sous-location'.`);
          }
      } catch (error: any) {
          console.error('Error parsing LBC URL:', error);
          if (error.message === 'EXTENSION_REQUIRED') {
              toast.error(
                  <div>
                      <p className="font-bold mb-2">Extension Chrome requise</p>
                      <p className="text-sm mb-2">Pour utiliser cette fonctionnalité, veuillez installer l'extension Chrome Placid Manager depuis le Chrome Web Store.</p>
                      <a 
                          href="https://help.babynlabs.com/fr/help/articles/0922162-utilisation-de-lextension-leboncoin" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline font-semibold text-sm flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                      >
                          <HelpCircle className="w-4 h-4" />
                          Consulter la documentation complète
                          <ExternalLink className="w-3 h-3" />
                      </a>
                  </div>,
                  { duration: 15000 }
              );
          } else if (error.message === 'EXTENSION_NOT_ACCESSIBLE') {
              toast.error(
                  <div>
                      <p className="font-bold mb-2">Extension non connectée</p>
                      <p className="text-sm mb-2">L'extension Chrome Placid Manager n'a pas été trouvée ou n'est pas configurée.</p>
                      <p className="text-sm mb-2">
                          Veuillez configurer l'ID de l'extension dans{' '}
                          <span className="font-semibold text-amber-600">Admin → Paramètres → Extension Chrome Leboncoin</span>.
                      </p>
                      <p className="text-xs text-slate-500 mb-2">
                          💡 Pour trouver l'ID : chrome://extensions/ → Mode développeur → Copier l'ID de "Placid Manager - Leboncoin Parser BÊTA"
                      </p>
                      <a 
                          href="https://help.babynlabs.com/fr/help/articles/0922162-utilisation-de-lextension-leboncoin" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline font-semibold text-sm flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                      >
                          <HelpCircle className="w-4 h-4" />
                          Consulter la documentation complète
                          <ExternalLink className="w-3 h-3" />
                      </a>
                  </div>,
                  { duration: 15000 }
              );
          } else if (error.message && error.message.includes('QUOTA_EXCEEDED')) {
              // Détecter l'erreur de quota
              const quotaMessage = error.message.replace('QUOTA_EXCEEDED: ', '');
              toast.error(quotaMessage, { duration: 10000 }); // Message plus long pour le quota
          } else {
              toast.error("Erreur lors du parsing de l'URL: " + (error.message || 'Erreur inconnue'));
          }
      } finally {
          setIsParsingLbc(false);
      }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = handlePhoneInputChange(e.target.value);
      setNewOwner({ ...newOwner, phone: val });
  };

  const handlePhoneBlur = () => {
      if (newOwner.phone) {
          setNewOwner({ ...newOwner, phone: formatPhoneForStorage(newOwner.phone) });
      }
  };

  if (!isOpen) return null;

  // --- LOGIQUE RECHERCHE PROPRIÉTAIRE ---
  const filteredOwners = searchQuery.length > 0 
      ? owners.filter(o => 
          `${o.firstName} ${o.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
          o.email.toLowerCase().includes(searchQuery.toLowerCase())
        ).slice(0, 5)
      : [];

  const handleSelectOwner = (owner: Owner) => {
      setSelectedOwner(owner);
      setSearchQuery('');
      setShowSuggestions(false);
      setMode('existing_owner');
      
      if (owner.agencyId) setDealData(prev => ({ ...prev, agencyId: owner.agencyId }));

      const ownerProps = properties.filter(p => p.ownerId === owner.id);
      if (ownerProps.length > 0) {
          setPropertyMode('existing_property');
          setSelectedPropertyId(ownerProps[0].id!);
      } else {
          setPropertyMode('new_property');
      }
  };

  const handleResetOwner = () => {
      setSelectedOwner(null);
      setMode('new_owner');
      setPropertyMode('new_property');
  };

  // --- LOGIQUE ADRESSE (API GOUV) ---
  const handleAddressSearch = async (query: string) => {
      setNewProperty({ ...newProperty, address: query });
      if (query.length > 3) {
          try {
              const res = await fetch(`https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(query)}&limit=5`);
              const data = await res.json();
              setAddressSuggestions(data.features || []);
          } catch (e) {
              console.error(e);
          }
      } else {
          setAddressSuggestions([]);
      }
  };

  const selectAddress = (feature: any) => {
      setNewProperty({
          ...newProperty,
          address: feature.properties.name,
          city: feature.properties.city,
          zipCode: feature.properties.postcode
      });
      setAddressSuggestions([]);
  };

  // --- SOUMISSION ---
  const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);

      try {
          // Vérifier si les données sont complètes avant de sauvegarder
          const isComplete = !hasIncompleteData;
          
          // Do not generate ID client-side to allow DB to generate UUID
          const dealPayload = { 
              ...dealData,
              needsCompletion: !isComplete, // Mettre à jour le statut de complétion
              createdAt: new Date().toISOString()
          } as Deal;

          if (mode === 'existing_owner' && selectedOwner) {
              dealPayload.ownerId = selectedOwner.id!;
              
              if (propertyMode === 'existing_property' && selectedPropertyId) {
                  dealPayload.propertyId = selectedPropertyId;
                  await onSave(dealPayload);
              } else if (propertyMode === 'new_property') {
                  // Créer un nouveau bien avec le propriétaire existant
                  const propPayload = { 
                      ...newProperty,
                      ownerId: selectedOwner.id!
                  } as Property;
                  await onSave(dealPayload, selectedOwner, propPayload);
              }
          } else {
              // Do not generate ID client-side
              const ownerPayload = { 
                  ...newOwner,
                  phone: newOwner.phone ? formatPhoneForStorage(newOwner.phone) : ''
              } as Owner;
              const propPayload = { ...newProperty } as Property;
              await onSave(dealPayload, ownerPayload, propPayload);
          }
          onClose();
      } catch (e) {
          console.error(e);
          toast.error("Erreur lors de la création.");
      } finally {
          setLoading(false);
      }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
        
        {/* HEADER */}
        <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-20">
          <div>
              <h3 className="text-2xl font-bold text-slate-900 flex items-center">
                <div className="bg-amber-100 p-2 rounded-lg mr-3">
                    <UserPlus className="w-6 h-6 text-amber-600" />
                </div>
                Nouveau Lead
              </h3>
              <p className="text-slate-500 text-sm mt-1 ml-14">Ajouter un prospect et son projet au CRM</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-2 rounded-full transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-8 overflow-y-auto flex-1 bg-slate-50/50">
            <form onSubmit={handleSubmit} className="space-y-8 max-w-2xl mx-auto">
                
                {/* ALERTE DONNÉES INCOMPLÈTES (LBC) - DISCRÈTE */}
                {hasIncompleteData && (
                    <div className="bg-amber-50/50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                            <span className="font-medium">Import Leboncoin : </span>
                            <span>Certaines informations n'ont pas pu être extraites automatiquement. Vous pourrez les compléter plus tard depuis la fiche du deal.</span>
                        </div>
                    </div>
                )}
                
                {/* IMPORT LEBONCOIN - BOUTON */}
                <div className="flex justify-center">
                    {leboncoinAnalysisComplete ? (
                        <button
                            type="button"
                            disabled
                            className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold rounded-lg opacity-75 cursor-not-allowed flex items-center gap-2"
                        >
                            <Sparkles className="w-5 h-5" />
                            Import et analyse IA terminée
                            <Check className="w-4 h-4" />
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setShowLeboncoinImport(!showLeboncoinImport)}
                            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold rounded-lg hover:from-orange-600 hover:to-amber-600 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                        >
                            <Globe className="w-5 h-5" />
                            {showLeboncoinImport ? 'Masquer l\'import Leboncoin' : 'Import Leboncoin'}
                            {showLeboncoinImport ? <ChevronDown className="w-4 h-4 rotate-180" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                    )}
                </div>

                {/* CHAMP URL LEBONCOIN (caché par défaut ou si analyse terminée) */}
                {showLeboncoinImport && !leboncoinAnalysisComplete && (
                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 rounded-xl border-2 border-orange-200 shadow-sm animate-in fade-in slide-in-from-top-2 duration-200">
                        <h5 className="text-sm font-bold text-orange-600 uppercase tracking-wider mb-3 flex items-center justify-between">
                            <div className="flex items-center">
                                <Globe className="w-4 h-4 mr-2" />
                                Import depuis Leboncoin
                            </div>
                            <a 
                                href="https://help.babynlabs.com/fr/help/articles/0922162-utilisation-de-lextension-leboncoin" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-orange-600 hover:text-orange-700 transition-colors"
                                title="Consulter la documentation"
                            >
                                <HelpCircle className="w-4 h-4" />
                            </a>
                        </h5>
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <input
                                    type="url"
                                    value={leboncoinUrl}
                                    onChange={(e) => setLeboncoinUrl(e.target.value)}
                                    onBlur={() => {
                                        if (leboncoinUrl && leboncoinUrl.includes('leboncoin.fr') && !isLeboncoinUrlReadOnly) {
                                            handleParseLeboncoinUrl(leboncoinUrl);
                                        }
                                    }}
                                    placeholder="Collez l'URL de l'annonce Leboncoin ici..."
                                    className="w-full px-4 py-3 border border-orange-300 rounded-lg bg-white text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                                    disabled={isParsingLbc || isLeboncoinUrlReadOnly}
                                    readOnly={isLeboncoinUrlReadOnly}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => handleParseLeboncoinUrl(leboncoinUrl)}
                                disabled={!leboncoinUrl || isParsingLbc || !leboncoinUrl.includes('leboncoin.fr')}
                                className="px-6 py-3 bg-orange-600 text-white font-bold rounded-lg hover:bg-orange-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                            >
                                {isParsingLbc ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Analyse...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="w-4 h-4" />
                                        Analyser avec AI
                                    </>
                                )}
                            </button>
                        </div>
                        <p className="text-xs text-orange-600 mt-2">
                            💡 L'annonce sera automatiquement liée et le type sera défini en "Sous-location"
                        </p>
                    </div>
                )}
                
                {/* 1. SELECTION PROPRIÉTAIRE */}
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center">
                        <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mr-2 text-xs">1</span>
                        Contact
                    </h4>
                    
                    {selectedOwner ? (
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex justify-between items-center">
                            <div className="flex items-center">
                                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold mr-4 text-lg">
                                    {selectedOwner.firstName[0]}
                                </div>
                                <div>
                                    <p className="font-bold text-blue-900 text-lg">{selectedOwner.firstName} {selectedOwner.lastName}</p>
                                    <p className="text-sm text-blue-700">{selectedOwner.email}</p>
                                </div>
                            </div>
                            <button type="button" onClick={handleResetOwner} className="text-sm font-bold text-blue-600 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors">Changer</button>
                        </div>
                    ) : (
                        <div className="space-y-5">
                            {/* Tabs */}
                            <div className="flex bg-slate-100 p-1 rounded-xl">
                                <button type="button" onClick={() => setMode('new_owner')} className={`flex-1 py-2.5 text-sm font-bold rounded-lg transition-all ${mode === 'new_owner' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                                    Nouveau Contact
                                </button>
                                <button type="button" onClick={() => setMode('existing_owner')} className={`flex-1 py-2.5 text-sm font-bold rounded-lg transition-all ${mode === 'existing_owner' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                                    Déjà existant
                                </button>
                            </div>

                            {mode === 'existing_owner' && (
                                <div className="relative" ref={searchRef}>
                                    <Search className="absolute left-4 top-3.5 w-5 h-5 text-slate-400" />
                                    <input 
                                        type="text" 
                                        className="w-full pl-12 p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none transition-all bg-white text-slate-900"
                                        placeholder="Rechercher (Nom, Email)..."
                                        value={searchQuery}
                                        onChange={(e) => { setSearchQuery(e.target.value); setShowSuggestions(true); }}
                                        onFocus={() => setShowSuggestions(true)}
                                        autoFocus
                                    />
                                    {showSuggestions && searchQuery && (
                                        <div className="absolute z-10 w-full bg-white border border-slate-200 rounded-xl mt-2 shadow-xl max-h-48 overflow-y-auto animate-in fade-in zoom-in-95 duration-100">
                                            {filteredOwners.map(owner => (
                                                <div key={owner.id} onClick={() => handleSelectOwner(owner)} className="p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0 flex items-center px-4">
                                                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-bold text-xs mr-3 text-slate-500">{owner.firstName[0]}{owner.lastName[0]}</div>
                                                    <div>
                                                        <p className="font-bold text-sm text-slate-800">{owner.firstName} {owner.lastName}</p>
                                                        <p className="text-xs text-slate-500">{owner.email}</p>
                                                    </div>
                                                </div>
                                            ))}
                                            {filteredOwners.length === 0 && <p className="p-4 text-sm text-slate-400 text-center">Aucun résultat trouvé.</p>}
                                        </div>
                                    )}
                                </div>
                            )}

                            {mode === 'new_owner' && (
                                <div className="grid grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2">
                                    <div className="col-span-1">
                                        <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">
                                            Prénom
                                        </label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                                            <input 
                                                required 
                                                className="w-full pl-9 p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white text-slate-900"
                                                placeholder="Jean" 
                                                value={newOwner.firstName} 
                                                onChange={e => setNewOwner({...newOwner, firstName: e.target.value})} 
                                            />
                                        </div>
                                    </div>
                                    <div className="col-span-1">
                                        <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Nom</label>
                                        <input required className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white text-slate-900" placeholder="Dupont" value={newOwner.lastName} onChange={e => setNewOwner({...newOwner, lastName: e.target.value})} />
                                    </div>
                                    <div className="col-span-1">
                                        <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">
                                            Email
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                                            <input 
                                                className="w-full pl-9 p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white text-slate-900"
                                                placeholder="email@exemple.com" 
                                                type="email" 
                                                value={newOwner.email} 
                                                onChange={e => setNewOwner({...newOwner, email: e.target.value})} 
                                            />
                                        </div>
                                    </div>
                                    <div className="col-span-1">
                                        <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">
                                            Téléphone
                                        </label>
                                        <div className="relative">
                                            <Phone className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                                            <input 
                                                className="w-full pl-9 p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none font-mono bg-white text-slate-900"
                                                placeholder="06 12 34 56 78" 
                                                type="tel" 
                                                value={newOwner.phone} 
                                                onChange={handlePhoneChange} 
                                                onBlur={handlePhoneBlur}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Source d'acquisition</label>
                                        <SearchableSelect
                                            options={['Site Web', 'Appel Entrant', 'Airbnb', 'Leboncoin', 'Recommandation', 'Réseau', 'Autre'].map(s => ({
                                                value: s,
                                                label: s
                                            }))}
                                            value={newOwner.source || ''}
                                            onChange={(value) => setNewOwner({...newOwner, source: value as LeadSource})}
                                            placeholder="Sélectionner une source"
                                            icon={<Globe className="w-4 h-4" />}
                                            searchPlaceholder="Rechercher une source..."
                                            showAllOnOpen={true}
                                        />
                                    </div>
                                    <div className="col-span-2 flex items-center space-x-2 p-2 bg-slate-50 rounded-lg border border-slate-200">
                                        <input
                                            type="checkbox"
                                            id="newsletter-optin-deal"
                                            checked={newOwner.newsletterOptin || false}
                                            onChange={(e) => setNewOwner({...newOwner, newsletterOptin: e.target.checked})}
                                            className="w-4 h-4 text-amber-600 border-slate-300 rounded focus:ring-amber-500 focus:ring-2"
                                        />
                                        <label htmlFor="newsletter-optin-deal" className="text-xs text-slate-600 cursor-pointer flex items-center">
                                            <Bell className="w-3 h-3 mr-1.5 text-amber-600" />
                                            Newsletter Placid
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* 2. LE BIEN */}
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center">
                        <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mr-2 text-xs">2</span>
                        Bien Immobilier
                    </h4>
                    
                    {mode === 'existing_owner' && (
                        <div className="flex bg-slate-100 p-1 rounded-xl mb-5">
                             <button type="button" onClick={() => setPropertyMode('existing_property')} className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${propertyMode === 'existing_property' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>Bien connu</button>
                             <button type="button" onClick={() => setPropertyMode('new_property')} className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${propertyMode === 'new_property' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>Nouveau Bien</button>
                        </div>
                    )}

                    {propertyMode === 'existing_property' && mode === 'existing_owner' ? (
                        <SearchableSelect
                            options={properties.filter(p => p.ownerId === selectedOwner?.id).map(p => ({
                                value: p.id!,
                                label: `${p.address}, ${p.city}`,
                                subtitle: `${p.type}${p.surface ? ` - ${p.surface}m²` : ''}`
                            }))}
                            value={selectedPropertyId}
                            onChange={(value) => setSelectedPropertyId(value)}
                            placeholder={properties.filter(p => p.ownerId === selectedOwner?.id).length === 0 ? 'Aucun bien enregistré' : 'Sélectionner un bien'}
                            icon={<Home className="w-5 h-5" />}
                            searchPlaceholder="Rechercher un bien..."
                            disabled={properties.filter(p => p.ownerId === selectedOwner?.id).length === 0}
                        />
                    ) : (
                        <div className="space-y-4 animate-in fade-in">
                            <div className="relative">
                                <label className="text-xs font-bold text-slate-500 mb-1 block uppercase flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    Adresse
                                </label>
                                <MapPin className="absolute left-4 top-9 w-5 h-5 text-slate-400" />
                                <input 
                                    className="w-full pl-12 p-3 border border-slate-300 rounded-xl bg-white text-slate-900 focus:ring-2 focus:ring-amber-500 outline-none"
                                    placeholder="Adresse (Recherche auto...)" 
                                    value={newProperty.address}
                                    onChange={e => handleAddressSearch(e.target.value)}
                                />
                                {addressSuggestions.length > 0 && (
                                    <ul className="absolute z-10 w-full bg-white border border-slate-200 rounded-xl mt-2 shadow-xl max-h-48 overflow-auto">
                                        {addressSuggestions.map((s, i) => (
                                            <li key={i} onClick={() => selectAddress(s)} className="p-3 hover:bg-slate-50 cursor-pointer text-sm border-b border-slate-50 last:border-0 text-slate-900">
                                                <span className="font-bold block">{s.properties.name}</span> 
                                                <span className="text-slate-500 text-xs">{s.properties.postcode} {s.properties.city}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <input className="p-2.5 border border-slate-200 rounded-lg bg-slate-50 text-slate-900 text-sm" placeholder="Code Postal" value={newProperty.zipCode} readOnly />
                                <input className="p-2.5 border border-slate-200 rounded-lg bg-slate-50 text-slate-900 text-sm" placeholder="Ville" value={newProperty.city} readOnly />
                            </div>
                            
                            <div className="grid grid-cols-4 gap-3">
                                <div className="col-span-2">
                                    <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block">Type</label>
                                    <SearchableSelect
                                        options={[
                                            { value: 'Appartement', label: 'Appartement' },
                                            { value: 'Maison', label: 'Maison' },
                                            { value: 'Immeuble', label: 'Immeuble' }
                                        ]}
                                        value={newProperty.type || 'Appartement'}
                                        onChange={(value) => setNewProperty({...newProperty, type: value as any})}
                                        placeholder="Sélectionner un type"
                                        icon={<Building className="w-4 h-4" />}
                                        searchPlaceholder="Rechercher un type..."
                                        className="p-2 text-sm"
                                        showAllOnOpen={true}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block">
                                        Surface (m²)
                                    </label>
                                    <div className="relative">
                                        <Ruler className="absolute left-2 top-2 w-4 h-4 text-slate-400" />
                                        <input 
                                            type="number" 
                                            className="w-full pl-8 p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm"
                                            value={newProperty.surface || ''} 
                                            onChange={e => setNewProperty({...newProperty, surface: Number(e.target.value)})} 
                                        />
                                    </div>
                                </div>
                                <div className="col-span-1">
                                    <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block">Ch.</label>
                                    <div className="relative">
                                        <BedDouble className="absolute left-2 top-2 w-3.5 h-3.5 text-slate-400" />
                                        <input type="number" className="w-full pl-7 p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm" value={newProperty.bedrooms || ''} onChange={e => setNewProperty({...newProperty, bedrooms: Number(e.target.value)})} />
                                    </div>
                                </div>
                                <div className="col-span-1">
                                    <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block">Sdb.</label>
                                    <div className="relative">
                                        <Bath className="absolute left-2 top-2 w-3.5 h-3.5 text-slate-400" />
                                        <input type="number" className="w-full pl-7 p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm" value={newProperty.bathrooms || ''} onChange={e => setNewProperty({...newProperty, bathrooms: Number(e.target.value)})} />
                                    </div>
                                </div>
                                <div className="col-span-2">
                                    <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block">Max Pers.</label>
                                    <div className="relative">
                                        <Users className="absolute left-2 top-2 w-4 h-4 text-slate-400" />
                                        <input type="number" className="w-full pl-8 p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm" value={newProperty.maxGuests || ''} onChange={e => setNewProperty({...newProperty, maxGuests: Number(e.target.value)})} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* 3. DÉTAILS DU DEAL */}
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center">
                        <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mr-2 text-xs">3</span>
                        Projet
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-6">
                        <div className="col-span-2">
                            <label className="block text-xs font-bold text-slate-500 mb-2 uppercase">Type</label>
                            <div className="grid grid-cols-2 gap-4">
                                <div 
                                    onClick={() => setDealData({...dealData, type: PipelineType.CONCIERGE})}
                                    className={`cursor-pointer p-4 rounded-xl border-2 flex flex-col items-center transition-all ${dealData.type === PipelineType.CONCIERGE ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                                >
                                    <Key className={`w-6 h-6 mb-2 ${dealData.type === PipelineType.CONCIERGE ? 'text-blue-600' : 'text-slate-400'}`} />
                                    <span className={`font-bold text-sm ${dealData.type === PipelineType.CONCIERGE ? 'text-blue-700' : 'text-slate-600'}`}>Conciergerie</span>
                                </div>
                                <div 
                                    onClick={() => setDealData({...dealData, type: PipelineType.SUBLET})}
                                    className={`cursor-pointer p-4 rounded-xl border-2 flex flex-col items-center transition-all ${dealData.type === PipelineType.SUBLET ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                                >
                                    <Briefcase className={`w-6 h-6 mb-2 ${dealData.type === PipelineType.SUBLET ? 'text-purple-600' : 'text-slate-400'}`} />
                                    <span className={`font-bold text-sm ${dealData.type === PipelineType.SUBLET ? 'text-purple-700' : 'text-slate-600'}`}>Sous-Location</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-span-2">
                            <label className="block text-xs font-bold text-slate-500 mb-2 uppercase">Potentiel Mensuel (€)</label>
                            <div className="relative">
                                <Wallet className="absolute left-4 top-3.5 w-5 h-5 text-slate-400" />
                                <input 
                                    type="number" 
                                    className="w-full pl-12 p-3 border border-slate-300 rounded-xl bg-white text-slate-900 font-bold text-lg focus:ring-2 focus:ring-amber-500 outline-none" 
                                    value={dealData.value || ''} 
                                    onChange={e => setDealData({...dealData, value: Number(e.target.value)})} 
                                    placeholder="0" 
                                />
                            </div>
                        </div>
                        
                        {dealData.type === PipelineType.SUBLET && (
                            <div className="col-span-2">
                                <label className="block text-xs font-bold text-slate-500 mb-2 uppercase">Loyer mensuel charges comprises (€)</label>
                                <div className="relative">
                                    <Wallet className="absolute left-4 top-3.5 w-5 h-5 text-purple-400" />
                                    <input 
                                        type="number" 
                                        className="w-full pl-12 p-3 border border-slate-300 rounded-xl bg-white text-slate-900 font-bold text-lg focus:ring-2 focus:ring-purple-500 outline-none" 
                                        value={dealData.monthlyRentWithCharges || ''} 
                                        onChange={e => setDealData({...dealData, monthlyRentWithCharges: Number(e.target.value) || undefined})} 
                                        placeholder="0" 
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    {canSelectAgency && (
                        <div className="mt-6 pt-4 border-t border-slate-100">
                            <label className="block text-xs font-bold text-slate-500 mb-2 uppercase">Agence de rattachement</label>
                            <SearchableSelect
                                options={[
                                    { value: '', label: 'Aucune agence' },
                                    ...agencies.map(a => ({
                                        value: a.id,
                                        label: `${a.city} (${a.name})`,
                                        subtitle: a.name
                                    }))
                                ]}
                                value={dealData.agencyId || ''}
                                onChange={(value) => setDealData({...dealData, agencyId: value})}
                                placeholder="Sélectionner une agence..."
                                icon={<Building className="w-5 h-5" />}
                                searchPlaceholder="Rechercher une agence..."
                                allowClear
                            />
                        </div>
                    )}
                </div>

            </form>
        </div>

        {/* FOOTER ACTIONS */}
        <div className="p-6 border-t border-slate-100 bg-white sticky bottom-0 z-20 flex justify-end space-x-4">
            <button onClick={onClose} className="px-6 py-3 text-slate-500 hover:bg-slate-100 rounded-xl font-bold transition-colors">
                Annuler
            </button>
            <button 
                onClick={handleSubmit} 
                disabled={
                    loading || 
                    (!selectedOwner && !newOwner.lastName) || 
                    (propertyMode === 'new_property' && !newProperty.address) ||
                    (propertyMode === 'existing_property' && mode === 'existing_owner' && !selectedPropertyId)
                }
                className="px-8 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold shadow-lg shadow-slate-900/20 disabled:opacity-50 transition-all flex items-center"
            >
                {loading ? 'Création...' : 'Créer le dossier'}
            </button>
        </div>

      </div>
    </div>
  );
};